import { AlertTriangle, ArrowLeft, Box, Briefcase, GitMerge, RefreshCw } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminPageLayout from "../../components/AdminPageLayout";
import { useTheme } from "../../contexts/ThemeContext";
import citriniService from "../../services/citriniService";
import { CitriniFetchInfoItem } from "../../types/citrini";
import FetchStatusBadge from "./CitriniImport/components/FetchStatusBadge";
import { ErrorBanner, formatDateCell, formatDuration, LoadingSpinner } from "./CitriniImport/helpers";
import BasketsFetchedTab from "./CitriniImport/tabs/BasketsFetchedTab";
import PortfoliosFetchedTab from "./CitriniImport/tabs/PortfoliosFetchedTab";
import SkippedContractsTab from "./CitriniImport/tabs/SkippedContractsTab";
import SyncResultsTab from "./CitriniImport/tabs/SyncResultsTab";

type Tab = "baskets" | "portfolios" | "sync" | "skipped";

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: "baskets", label: "Baskets Fetched", icon: Box },
  { id: "portfolios", label: "Portfolios Fetched", icon: Briefcase },
  { id: "sync", label: "Sync Results", icon: GitMerge },
  { id: "skipped", label: "Skipped Contracts", icon: AlertTriangle },
];

const CitriniImportDetailPage: React.FC = () => {
  const { fetchInfoId } = useParams<{ fetchInfoId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [activeTab, setActiveTab] = useState<Tab>("baskets");
  const [tabKey, setTabKey] = useState(0);

  // Fetch info state
  const [fetchInfo, setFetchInfo] = useState<CitriniFetchInfoItem | null>(
    (location.state as { fetchInfo?: CitriniFetchInfoItem } | null)?.fetchInfo ?? null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabCounts, setTabCounts] = useState<Record<Tab, number | null>>({
    baskets: null,
    portfolios: null,
    sync: null,
    skipped: null,
  });

  const handleCountChange = useCallback((tab: Tab, count: number) => {
    setTabCounts((prev) => ({ ...prev, [tab]: count }));
  }, []);

  const fetchInfoIdNum = parseInt(fetchInfoId || "0", 10);

  const loadFetchInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await citriniService.listFetchInfo({ page: 1, pageSize: 100, orderDir: "desc" });
      const found = res.items.find((item) => item.id === fetchInfoIdNum);
      if (found) {
        setFetchInfo(found);
      } else {
        setError(`Fetch info #${fetchInfoIdNum} not found`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load fetch info");
    } finally {
      setLoading(false);
    }
  }, [fetchInfoIdNum]);

  // On mount: use navigation state if available, otherwise fetch from API
  useEffect(() => {
    if (!fetchInfo) {
      loadFetchInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <AdminPageLayout title={`Fetch #${fetchInfoIdNum}`} description="">
        <LoadingSpinner isDarkMode={isDarkMode} />
      </AdminPageLayout>
    );
  }

  if (error || !fetchInfo) {
    return (
      <AdminPageLayout title={`Fetch #${fetchInfoIdNum}`} description="">
        {error ? (
          <ErrorBanner message={error} onRetry={loadFetchInfo} isDarkMode={isDarkMode} />
        ) : null}
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title={`Fetch #${fetchInfoIdNum}`}
      description="Citrini fetch run details"
      actions={
        <button
          onClick={() => { loadFetchInfo(); setTabKey((k) => k + 1); }}
          className={`px-4 py-2 text-sm font-medium rounded-lg border flex items-center ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      }
      headerContent={
        <div className="space-y-3">
          {/* Back button */}
          <button
            onClick={() => navigate("/admin/citrini-import")}
            className={`flex items-center text-sm ${
              isDarkMode
                ? "text-rp-grey/50 hover:text-rp-grey"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Fetch History
          </button>

          {/* Info card */}
          <div
            className={`rounded-lg border p-4 ${
              isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
            }`}
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <InfoCell label="ID" value={String(fetchInfo.id)} isDarkMode={isDarkMode} />
              <InfoCell
                label="Started At (UTC)"
                value={formatDateCell(fetchInfo.startedAtUtc)}
                isDarkMode={isDarkMode}
              />
              <InfoCell
                label="Finished At (UTC)"
                value={formatDateCell(fetchInfo.finishedAtUtc)}
                isDarkMode={isDarkMode}
              />
              <InfoCell
                label="Duration"
                value={formatDuration(fetchInfo.startedAtUtc, fetchInfo.finishedAtUtc)}
                isDarkMode={isDarkMode}
              />
              <div>
                <p
                  className={`text-xs mb-1 ${
                    isDarkMode ? "text-rp-grey/40" : "text-gray-400"
                  }`}
                >
                  Status
                </p>
                <FetchStatusBadge
                  startedAtUtc={fetchInfo.startedAtUtc}
                  finishedAtUtc={fetchInfo.finishedAtUtc}
                />
              </div>
            </div>
          </div>
        </div>
      }
    >
      {/* Tabs */}
      <div
        className={`flex space-x-1 border-b mb-4 ${
          isDarkMode ? "border-rp-separator" : "border-gray-200"
        }`}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                isActive
                  ? isDarkMode
                    ? "border-blue-500 text-blue-400"
                    : "border-blue-600 text-blue-600"
                  : isDarkMode
                    ? "border-transparent text-rp-grey/50 hover:text-rp-grey/70 hover:border-rp-separator"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {tab.label}
              {tabCounts[tab.id] !== null && (
                <span className={`ml-1.5 text-xs ${isActive ? "" : "opacity-70"}`}>
                  ({tabCounts[tab.id]})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "baskets" && (
        <BasketsFetchedTab
          key={`baskets-${tabKey}`}
          fetchInfoId={fetchInfoIdNum}
          isDarkMode={isDarkMode}
          onCountChange={(count) => handleCountChange("baskets", count)}
        />
      )}
      {activeTab === "portfolios" && (
        <PortfoliosFetchedTab
          key={`portfolios-${tabKey}`}
          fetchInfoId={fetchInfoIdNum}
          isDarkMode={isDarkMode}
          onCountChange={(count) => handleCountChange("portfolios", count)}
        />
      )}
      {activeTab === "sync" && (
        <SyncResultsTab
          key={`sync-${tabKey}`}
          fetchInfoId={fetchInfoIdNum}
          fetchInfo={fetchInfo}
          isDarkMode={isDarkMode}
          onCountChange={(count) => handleCountChange("sync", count)}
        />
      )}
      {activeTab === "skipped" && (
        <SkippedContractsTab
          key={`skipped-${tabKey}`}
          fetchInfoId={fetchInfoIdNum}
          isDarkMode={isDarkMode}
          onCountChange={(count) => handleCountChange("skipped", count)}
        />
      )}
    </AdminPageLayout>
  );
};

const InfoCell: React.FC<{ label: string; value: string; isDarkMode: boolean }> = ({
  label,
  value,
  isDarkMode,
}) => (
  <div>
    <p className={`text-xs mb-1 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>{label}</p>
    <p className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-800"}`}>
      {value}
    </p>
  </div>
);

export default CitriniImportDetailPage;
